import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Divider,
  Button,
  Avatar,
  Stack,
} from '@mui/material';
import PublicHeader from '../../Components/Header/PublicHeader';
import SiteFooter from '../../Components/Footer/SiteFooter';
import { SIGNUP_ENABLED } from '../../config/prelaunch';
import AppleSignInButton from '../../Components/Auth/AppleSignInButton';
import {
  isAppleLoginEnabled,
  isAppleSignInCancel,
  signInWithApple,
} from '../../utils/appleAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL as string;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID as string;
const TOKEN_EXPIRATION_DAYS = 7;

// 法務・プライバシー文書（feast-corp に多言語で掲示・ADR 0006）
const TERMS_URL = 'https://corp.feast.co.jp/legal/terms/';
const PRIVACY_URL = 'https://corp.feast.co.jp/legal/privacy/';

interface GoogleCredentialResponse {
  credential: string;
}

// 郵便番号は 2 分割（上3桁-下4桁）、電話番号は 3 分割で入力し、送信時に `-` で連結する。
interface RegisterForm {
  companyName: string;
  representativeLastName: string;
  representativeFirstName: string;
  companyPostal1: string;
  companyPostal2: string;
  companyAddress: string;
  companyPhone1: string;
  companyPhone2: string;
  companyPhone3: string;
  storeName: string;
  storeManagerLastName: string;
  storeManagerFirstName: string;
  storePostal1: string;
  storePostal2: string;
  storeAddress: string;
  storePhone1: string;
  storePhone2: string;
  storePhone3: string;
}

const EMPTY_FORM: RegisterForm = {
  companyName: '',
  representativeLastName: '',
  representativeFirstName: '',
  companyPostal1: '',
  companyPostal2: '',
  companyAddress: '',
  companyPhone1: '',
  companyPhone2: '',
  companyPhone3: '',
  storeName: '',
  storeManagerLastName: '',
  storeManagerFirstName: '',
  storePostal1: '',
  storePostal2: '',
  storeAddress: '',
  storePhone1: '',
  storePhone2: '',
  storePhone3: '',
};

// すべて必須。空チェックに使う [値のキー, ラベル] 一覧。
const REQUIRED_FIELDS: [keyof RegisterForm, string][] = [
  ['companyName', '企業名'],
  ['representativeLastName', '代表者 氏（姓）'],
  ['representativeFirstName', '代表者 名'],
  ['companyPostal1', '企業の郵便番号（上3桁）'],
  ['companyPostal2', '企業の郵便番号（下4桁）'],
  ['companyAddress', '企業住所'],
  ['companyPhone1', '企業電話番号（市外局番）'],
  ['companyPhone2', '企業電話番号（中）'],
  ['companyPhone3', '企業電話番号（下）'],
  ['storeName', '店舗名'],
  ['storeManagerLastName', '店舗責任者 氏（姓）'],
  ['storeManagerFirstName', '店舗責任者 名'],
  ['storePostal1', '店舗の郵便番号（上3桁）'],
  ['storePostal2', '店舗の郵便番号（下4桁）'],
  ['storeAddress', '店舗住所'],
  ['storePhone1', '店舗電話番号（市外局番）'],
  ['storePhone2', '店舗電話番号（中）'],
  ['storePhone3', '店舗電話番号（下）'],
];

/** ID トークン（JWT）の payload から表示用プロフィールを取り出す（Google/Apple 共通）。検証は BE が行う。 */
const decodeIdTokenProfile = (credential: string): { email?: string; name?: string; picture?: string } => {
  try {
    const payload = credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      window
        .atob(payload)
        .split('')
        .map((ch) => `%${`00${ch.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
};

/**
 * 公開登録フォーム（企業 + 店舗の新規作成）。要件定義 0001 §4 / feast-fe ADR 0004・0032。
 * フローは Google ログイン先行の 2 ステップ:
 *   1. Google アカウントでサインイン（本人確認）
 *   2. 企業・店舗情報を入力して登録（保存済み idToken を添えて POST /public/signup）
 * 郵便番号・電話番号は分割フィールドで入力し、`-` で連結して送信する（DB はハイフン付きで保存）。
 */
const RegisterInner: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<RegisterForm>(EMPTY_FORM);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credential, setCredential] = useState<string | null>(null);
  const [profile, setProfile] = useState<{ email?: string; name?: string; picture?: string }>({});
  const [provider, setProvider] = useState<'google' | 'apple'>('google');
  const [gsiReady, setGsiReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (key: keyof RegisterForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  // GIS スクリプトの読み込み（初回のみ）。
  useEffect(() => {
    if (window.google?.accounts?.id) {
      setGsiReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => setGsiReady(true);
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // ステップ1（未サインイン）の間だけ Google ボタンを描画する。
  // 「別のアカウントでやり直す」で credential が null に戻ったときも再描画する。
  useEffect(() => {
    if (!gsiReady || credential) return;
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response: GoogleCredentialResponse) => {
        setCredential(response.credential);
        setProfile(decodeIdTokenProfile(response.credential));
        setProvider('google');
        setError(null);
      },
    });
    window.google.accounts.id.renderButton(document.getElementById('google-signup-button'), {
      theme: 'filled_blue',
      size: 'large',
      shape: 'pill',
      text: 'signup_with',
    });
  }, [gsiReady, credential]);

  // Apple サインアップ（ADR 0036）。初回認可時のみ user.name が取れるため profile に保持して送信する。
  const handleAppleSignIn = (): void => {
    setError(null);
    signInWithApple()
      .then((result) => {
        setCredential(result.idToken);
        setProfile({
          email: decodeIdTokenProfile(result.idToken).email,
          name: result.name,
        });
        setProvider('apple');
      })
      .catch((e: unknown) => {
        if (isAppleSignInCancel(e)) return;
        setError('Apple サインインに失敗しました。時間をおいて再度お試しください。');
      });
  };

  const handleSubmit = (): void => {
    if (!credential) {
      setError('先に Google または Apple アカウントでサインインしてください。');
      return;
    }
    const missing = REQUIRED_FIELDS.filter(([k]) => !form[k].trim()).map(([, label]) => label);
    if (missing.length > 0) {
      setError(`次の項目を入力してください: ${missing.join('、')}`);
      return;
    }
    if (!agreed) {
      setError('利用規約とプライバシーポリシーへの同意が必要です。');
      return;
    }
    setError(null);
    setSubmitting(true);

    // 分割入力を `-` で連結（入力側で付与）。DB にはこの連結値が保存される。
    const companyPostalCode = `${form.companyPostal1.trim()}-${form.companyPostal2.trim()}`;
    const companyPhone = `${form.companyPhone1.trim()}-${form.companyPhone2.trim()}-${form.companyPhone3.trim()}`;
    const storePostalCode = `${form.storePostal1.trim()}-${form.storePostal2.trim()}`;
    const storePhone = `${form.storePhone1.trim()}-${form.storePhone2.trim()}-${form.storePhone3.trim()}`;

    fetch(`${API_BASE_URL}/public/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idToken: credential,
        // Apple の場合のみ provider を明示（省略時の BE 既定は google）。
        // name は初回認可時に取得できた場合のみ送る（BE 側は代表者氏名にフォールバック）。
        ...(provider === 'apple'
          ? { provider: 'apple', ...(profile.name ? { name: profile.name } : {}) }
          : {}),
        companyName: form.companyName.trim(),
        representativeLastName: form.representativeLastName.trim(),
        representativeFirstName: form.representativeFirstName.trim(),
        companyPostalCode,
        companyAddress: form.companyAddress.trim(),
        companyPhone,
        storeName: form.storeName.trim(),
        storeManagerLastName: form.storeManagerLastName.trim(),
        storeManagerFirstName: form.storeManagerFirstName.trim(),
        storePostalCode,
        storeAddress: form.storeAddress.trim(),
        storePhone,
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || '登録に失敗しました。時間をおいて再度お試しください。');
        }
        return data;
      })
      .then((data) => {
        if (data.accessToken) {
          const expiration = new Date().getTime() + TOKEN_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
          localStorage.setItem('accessToken', data.accessToken);
          localStorage.setItem('refreshToken', data.refreshToken);
          localStorage.setItem('tokenExpiration', expiration.toString());
          localStorage.setItem('email', data.email);
          localStorage.setItem('name', data.name);
          localStorage.setItem('picture', data.picture);
          localStorage.setItem('clerk_id', data.clerkId);
          navigate(`/home?storeClerkId=${data.clerkId}`);
        } else {
          setError('登録に失敗しました。時間をおいて再度お試しください。');
        }
      })
      .catch((e: Error) => {
        setError(e.message);
      })
      .finally(() => setSubmitting(false));
  };

  // 分割フィールド用の小さめ TextField。
  const partField = (key: keyof RegisterForm, label: string, maxLength: number) => (
    <TextField
      label={label}
      value={form[key]}
      onChange={update(key)}
      margin="dense"
      size="small"
      inputProps={{ maxLength, inputMode: 'numeric' }}
      required
      sx={{ flex: 1 }}
    />
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <Container component="main" maxWidth="sm" sx={{ flex: 1 }}>
        <Box sx={{ marginTop: 4, marginBottom: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <Typography component="h1" variant="h5" gutterBottom>
              FEAST に新規登録する
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            {!credential ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  まず Google または Apple アカウントでサインインしてください。
                  続けて企業・店舗情報を入力すると登録が完了します（30日間の無料トライアル付き）。
                </Typography>
                <Box id="google-signup-button" sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 1 }} />
                {isAppleLoginEnabled() && (
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                    <AppleSignInButton label="Appleで登録" onClick={handleAppleSignIn} />
                  </Box>
                )}
              </>
            ) : (
              <>
                <Alert severity="success" icon={false} sx={{ width: '100%', mb: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {profile.picture && <Avatar src={profile.picture} sx={{ width: 32, height: 32 }} />}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {profile.name || (provider === 'apple' ? 'Apple アカウント' : 'Google アカウント')} でサインイン中
                      </Typography>
                      {profile.email && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {profile.email}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" onClick={() => { setCredential(null); setProfile({}); setProvider('google'); }}>
                      変更
                    </Button>
                  </Stack>
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, width: '100%' }}>
                  企業アカウントと最初の店舗を作成します。登録後、店舗の課金はアプリ内でお手続きください
                  （30日間の無料トライアル付き）。
                </Typography>

                <Typography variant="subtitle2" sx={{ width: '100%', mt: 1 }}>
                  企業情報
                </Typography>
                <TextField label="企業名" value={form.companyName} onChange={update('companyName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} required />
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <TextField label="代表者 氏（姓）" value={form.representativeLastName} onChange={update('representativeLastName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} required />
                  <TextField label="代表者 名" value={form.representativeFirstName} onChange={update('representativeFirstName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} required />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {partField('companyPostal1', '郵便番号 上3桁', 3)}
                  <Typography>-</Typography>
                  {partField('companyPostal2', '下4桁', 4)}
                </Box>
                <TextField label="企業住所" value={form.companyAddress} onChange={update('companyAddress')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 255 }} required />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {partField('companyPhone1', '電話 市外局番', 5)}
                  <Typography>-</Typography>
                  {partField('companyPhone2', '中', 4)}
                  <Typography>-</Typography>
                  {partField('companyPhone3', '下', 4)}
                </Box>

                <Divider sx={{ width: '100%', my: 2 }} />
                <Typography variant="subtitle2" sx={{ width: '100%' }}>
                  店舗情報
                </Typography>
                <TextField label="店舗名" value={form.storeName} onChange={update('storeName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} required />
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <TextField label="店舗責任者 氏（姓）" value={form.storeManagerLastName} onChange={update('storeManagerLastName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} required />
                  <TextField label="店舗責任者 名" value={form.storeManagerFirstName} onChange={update('storeManagerFirstName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} required />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {partField('storePostal1', '郵便番号 上3桁', 3)}
                  <Typography>-</Typography>
                  {partField('storePostal2', '下4桁', 4)}
                </Box>
                <TextField label="店舗住所" value={form.storeAddress} onChange={update('storeAddress')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 255 }} required />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {partField('storePhone1', '電話 市外局番', 5)}
                  <Typography>-</Typography>
                  {partField('storePhone2', '中', 4)}
                  <Typography>-</Typography>
                  {partField('storePhone3', '下', 4)}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mt: 0.5 }}>
                  ※ 店舗の電話番号は、予約確認などお客様・スタッフ向けに送信するメールに記載されます。
                </Typography>

                <FormControlLabel
                  sx={{ mt: 2, alignItems: 'flex-start', width: '100%' }}
                  control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} size="small" />}
                  label={
                    <Typography variant="body2">
                      <Link href={TERMS_URL} target="_blank" rel="noopener">利用規約</Link>
                      {' と '}
                      <Link href={PRIVACY_URL} target="_blank" rel="noopener">プライバシーポリシー</Link>
                      {' に同意します'}
                    </Typography>
                  }
                />

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? '登録中…' : '登録する'}
                </Button>
              </>
            )}

            <Divider sx={{ width: '100%', my: 2 }} />
            <Typography variant="body2">
              すでにアカウントをお持ちの方は{' '}
              <Link component="button" type="button" onClick={() => navigate('/login')}>
                ログイン
              </Link>
            </Typography>
          </Paper>
        </Box>
      </Container>
      <SiteFooter />
    </Box>
  );
};

// 新規登録受付ゲート（ADR 0059 / 0060）。本番（SIGNUP_ENABLED=false）はローンチ前のため
// 登録動線を出さない方針。直接 /register にアクセスされてもトップへリダイレクトする。
// stg/local は SIGNUP_ENABLED=true で登録フォームを表示する。
const Register: React.FC = () =>
  SIGNUP_ENABLED ? <RegisterInner /> : <Navigate to="/" replace />;

export default Register;
